import React, { useState, useEffect, useMemo } from 'react';
import { Nav } from 'react-bootstrap';
import { Link, useLocation } from 'react-router-dom';
import { 
  House, 
  Envelope, 
  FileText, 
  People, 
  BarChart, 
  Globe, 
  EnvelopeAt,
  Cloud,
  Activity,
  ArrowClockwise,
  Share,
  PersonGear,
  Person,
  PersonWorkspace,
  Grid3x3,
  Server,
  Shield,
  Gear,
  PersonCheck,
  Briefcase,
  CalendarEvent,
  ListTask,
  PersonVcard,
  Building,
  XCircle,
  BriefcaseFill,
  CashStack,
  GraphUp,
  Wallet2,
  PieChart,
  Bullseye
} from 'react-bootstrap-icons';
import './Sidebar.css';
import Logo from './Logo';
import { useGetUserPreferencesQuery } from '../services/api';
import { useAccount } from '../contexts/AccountContext';
import { useTenantIdForPreferences } from '../hooks/useTenantIdForPreferences';

interface NavItem {
  path: string;
  label: string;
  icon: React.ComponentType<{ className?: string; size?: number }>;
}

interface SidebarProps {
  show?: boolean;
  onClose?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ show = false, onClose }) => {
  const location = useLocation();
  const accountContext = useAccount();
  
  // Determine which section should be open based on current location
  const getInitialOpenState = (section: string) => {
    const path = location.pathname;
    switch (section) {
      case 'crm':
        return ['/clients', '/leads', '/deals', '/meetings', '/contacts', '/activities', '/target-accounts'].some(route =>
          path === route || path.startsWith('/clients/') || path.startsWith('/leads/') || path.startsWith('/deals/') || path.startsWith('/meetings/') || path.startsWith('/contacts/') || path.startsWith('/target-accounts/')
        );
      case 'emm':
        // Keep this list aligned with every EMM child rendered under the EMM
        // dropdown: pinned items (Unibox, Executive Assistant) + all sub-group
        // items (Campaign Ops / Setup / Insights). Missing entries here cause
        // the EMM section to collapse on navigation, which hides the active
        // child highlight.
        return ['/emm-dashboard', '/agents', '/campaigns', '/templates', '/subscribers', '/analytics', '/block-list', '/bounced-list', '/unibox', '/executive-assistant', '/send-pools', '/relay-accounts', '/rules-engine', '/cross-identity', '/health-monitoring', '/rotation-scheduling'].some(route =>
          path === route || path.startsWith('/campaigns/') || path.startsWith('/templates/') || path.startsWith('/executive-assistant/')
        );
      case 'erm':
        return ['/erm/projects', '/erm/departments', '/erm/templates', '/erm/archive', '/erm/tasks'].some((route) =>
          path === route || path.startsWith(`${route}/`)
        );
      case 're':
        return ['/re/expenses', '/re/revenue', '/re/overview'].some(route =>
          path === route || path.startsWith('/re/')
        );
      case 'identity':
        return ['/aws-accounts', '/domains', '/email-accounts', '/admin/email-platform-status', '/rotation-scheduling', '/cross-identity', '/health-monitoring'].some(route => 
          path === route || path.startsWith('/aws-accounts/') || path.startsWith('/domains/') || path.startsWith('/email-accounts/')
        );
      case 'admin':
        return ['/users', '/profile', '/admin/company-profile'].some(route => 
          path === route || path.startsWith('/users/')
        );
      default:
        return false;
    }
  };

  const [crmOpen, setCrmOpen] = useState(getInitialOpenState('crm'));
  const [emmOpen, setEmmOpen] = useState(getInitialOpenState('emm'));
  const [ermOpen, setErmOpen] = useState(getInitialOpenState('erm'));
  const [reOpen, setReOpen] = useState(getInitialOpenState('re'));
  const [identityOpen, setIdentityOpen] = useState(getInitialOpenState('identity'));
  const [adminOpen, setAdminOpen] = useState(getInitialOpenState('admin'));

  const campaignOpsPaths = ['/block-list', '/bounced-list', '/campaigns', '/cross-identity', '/health-monitoring', '/rotation-scheduling', '/subscribers', '/templates'];
  // Executive Assistant is rendered as a pinned top-level EMM child (alongside
  // Unibox), NOT inside the Setup sub-group. Listing it here would incorrectly
  // expand Setup whenever you navigate to /executive-assistant, even though
  // the Setup sub-group never contains an EA item to highlight.
  const setupPaths = ['/agents', '/relay-accounts', '/rules-engine', '/send-pools'];
  const insightsPaths = ['/analytics', '/emm-dashboard'];

  const getEmmSubgroupOpen = (group: 'campaignOps' | 'setup' | 'insights') => {
    const path = location.pathname;
    switch (group) {
      case 'campaignOps':
        return campaignOpsPaths.some((r) => path === r || path.startsWith(r + '/')) || !setupPaths.concat(insightsPaths).some((r) => path === r || path.startsWith(r + '/'));
      case 'setup':
        return setupPaths.some((r) => path === r || path.startsWith(r + '/'));
      case 'insights':
        return insightsPaths.some((r) => path === r || path.startsWith(r + '/'));
      default:
        return false;
    }
  };

  const [emmCampaignOpsOpen, setEmmCampaignOpsOpen] = useState(getEmmSubgroupOpen('campaignOps'));
  const [emmSetupOpen, setEmmSetupOpen] = useState(getEmmSubgroupOpen('setup'));
  const [emmInsightsOpen, setEmmInsightsOpen] = useState(getEmmSubgroupOpen('insights'));

  const tenantIdForPrefs = useTenantIdForPreferences();

  const { data: userPrefsData, isFetching: isFetchingUserPrefs } = useGetUserPreferencesQuery(tenantIdForPrefs ? { tenantId: tenantIdForPrefs } : undefined, {
    refetchOnMountOrArgChange: true,
  });
  const companyProfile: any = (userPrefsData as any)?.preferences?.companyProfile || {};
  const companyLogoUrl: string | null = companyProfile?.logoUrl ? String(companyProfile.logoUrl) : null;
  const companyName: string = companyProfile?.companyName ? String(companyProfile.companyName) : '';
  const hasResolvedCompanyPrefs = Boolean(userPrefsData) || !isFetchingUserPrefs;
  const [isLogoFlipped, setIsLogoFlipped] = useState(false);

  // Update open state when route changes
  useEffect(() => {
    setCrmOpen(getInitialOpenState('crm'));
    setEmmOpen(getInitialOpenState('emm'));
    setErmOpen(getInitialOpenState('erm'));
    setReOpen(getInitialOpenState('re'));
    setIdentityOpen(getInitialOpenState('identity'));
    setAdminOpen(getInitialOpenState('admin'));
    if (getInitialOpenState('emm')) {
      setEmmCampaignOpsOpen(getEmmSubgroupOpen('campaignOps'));
      setEmmSetupOpen(getEmmSubgroupOpen('setup'));
      setEmmInsightsOpen(getEmmSubgroupOpen('insights'));
    }
    onClose?.(); // Close mobile sidebar when route changes
  }, [location.pathname, onClose]);

  // Function to handle dropdown clicks - close all others when one opens
  const handleDropdownClick = (dropdownName: string) => {
    setCrmOpen(dropdownName === 'crm' ? (prev) => !prev : false);
    setEmmOpen(dropdownName === 'emm' ? (prev) => !prev : false);
    setErmOpen(dropdownName === 'erm' ? (prev) => !prev : false);
    setReOpen(dropdownName === 're' ? (prev) => !prev : false);
    setIdentityOpen(dropdownName === 'identity' ? (prev) => !prev : false);
    setAdminOpen(dropdownName === 'admin' ? (prev) => !prev : false);
  };

  const crmNavItems: NavItem[] = [
    { path: '/clients', label: 'Clients', icon: Building },
    { path: '/contacts', label: 'Contacts', icon: PersonVcard },
    { path: '/deals', label: 'Deals', icon: Briefcase },
    { path: '/leads', label: 'Leads', icon: PersonCheck },
    { path: '/target-accounts', label: 'Target Accounts', icon: Bullseye },
    { path: '/activities', label: 'Activities', icon: ListTask },
    { path: '/meetings', label: 'Meetings', icon: CalendarEvent },
  ];

  const emmCampaignOpsItems: NavItem[] = [
    { path: '/block-list', label: 'Block List', icon: XCircle },
    { path: '/bounced-list', label: 'Bounced List', icon: Envelope },
    { path: '/campaigns', label: 'Campaigns', icon: Envelope },
    { path: '/cross-identity', label: 'Cross-Identity', icon: Share },
    { path: '/health-monitoring', label: 'Health Monitor', icon: Activity },
    { path: '/rotation-scheduling', label: 'Rotation', icon: ArrowClockwise },
    { path: '/subscribers', label: 'Subscribers', icon: People },
    { path: '/templates', label: 'Templates', icon: FileText },
  ];

  const emmSetupItems: NavItem[] = [
    { path: '/agents', label: 'Agents', icon: People },
    { path: '/relay-accounts', label: 'Relay Accounts', icon: Shield },
    { path: '/rules-engine', label: 'Rules Engine', icon: Grid3x3 },
    { path: '/send-pools', label: 'Send Pools', icon: Gear },
  ];

  const emmInsightsItems: NavItem[] = [
    { path: '/analytics', label: 'Analytics', icon: BarChart },
    { path: '/emm-dashboard', label: 'EMM Dashboard', icon: House },
  ];

  const identityNavItems: NavItem[] = [
    { path: '/domains', label: 'Domains', icon: Globe },
    { path: '/email-accounts', label: 'Email Accounts', icon: EnvelopeAt },
    { path: '/admin/email-platform-status', label: 'Email Server Status', icon: Server },
    { path: '/aws-accounts', label: 'Hosting Management', icon: Cloud },
  ];

  const adminNavItems: NavItem[] = [
    { path: '/admin/company-profile', label: 'Company Profile', icon: Building },
    { path: '/profile', label: 'My Profile', icon: Person },
    { path: '/users', label: 'User Management', icon: PersonGear },
  ];

  const ermNavItems: NavItem[] = [
    { path: '/erm/projects', label: 'Projects', icon: Briefcase },
    { path: '/erm/departments', label: 'Departments', icon: Building },
    { path: '/erm/templates', label: 'Templates', icon: FileText },
    { path: '/erm/archive', label: 'Archive', icon: XCircle },
    { path: '/erm/tasks', label: 'Tasks', icon: CalendarEvent },
  ];

  const reNavItems: NavItem[] = [
    { path: '/re/expenses', label: 'Expenses', icon: Wallet2 },
    { path: '/re/overview', label: 'Financial Overview', icon: PieChart },
    { path: '/re/revenue', label: 'Revenue', icon: GraphUp },
  ];

  const hasExpandedSidebarSection = crmOpen || emmOpen || ermOpen || reOpen || identityOpen || adminOpen;
  const sidebarNavClassName = hasExpandedSidebarSection
    ? 'sidebar-nav sidebar-nav--expanded'
    : 'sidebar-nav sidebar-nav--collapsed';

  return (
    <>
      {show && (
        <div
          className="sidebar-backdrop d-lg-none"
          style={{ position: 'fixed', inset: 0, top: 56, zIndex: 999, backgroundColor: 'rgba(0,0,0,0.5)', cursor: 'pointer' }}
          onClick={onClose}
          onTouchEnd={(e) => { e.preventDefault(); onClose?.(); }}
          aria-hidden="true"
        />
      )}
    <div className={`sidebar ${show ? 'show' : ''}`}>
      <div className="sidebar-content">
        <div className="sidebar-header">
          <Logo size="medium" showText={true} />
        </div>
        
        <Nav className={sidebarNavClassName}>
          {/* Dashboard - Standalone at top */}
          <div className="nav-section">
            <Nav.Link
              as={Link}
              to="/dashboard"
              className={`nav-link ${
                location.pathname === '/dashboard' || location.pathname === '/' ? 'nav-link--active' : ''
              }`}
              style={{ padding: '8px 10px', marginBottom: '0.5rem' }}
            >
              <House className="nav-icon" size={16} />
              Dashboard
            </Nav.Link>
          </div>

          {/* CRM Section Dropdown */}
          <div className="nav-section">
            <div 
              className={`sidebar-dropdown${crmOpen ? ' active' : ''}`}
              onClick={() => handleDropdownClick('crm')}
              style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', padding: '8px 10px', marginBottom: '0.5rem' }}
            >
              <BriefcaseFill size={14} style={{ marginRight: '8px', color: 'rgba(255, 255, 255, 0.6)' }} />
              <h6 className="nav-section-title" style={{ margin: 0, flex: 1 }}>
                CRM <span style={{ fontSize: '0.5rem', fontWeight: 'normal', opacity: 0.7 }}>(Client MGMT)</span>
              </h6>
              <span style={{ 
                marginLeft: 'auto', 
                transition: 'transform 0.2s', 
                transform: crmOpen ? 'rotate(180deg)' : 'none',
                fontSize: '0.6rem',
                color: 'rgba(255, 255, 255, 0.6)'
              }}>▲</span>
            </div>
            {crmOpen && (
              <div className="sidebar-submenu">
                {crmNavItems.map((item) => {
                  const IconComponent = item.icon;
                  return (
                    <Nav.Link
                      key={item.path}
                      as={Link}
                      to={item.path}
                      className={`nav-link ${
                        location.pathname === item.path ? 'nav-link--active' : ''
                      }`}
                    >
                      <IconComponent className="nav-icon" size={16} />
                      {item.label}
                    </Nav.Link>
                  );
                })}
              </div>
            )}
          </div>

          {/* EMM (Email Marketing) Section Dropdown */}
          <div className="nav-section">
            <div 
              className={`sidebar-dropdown${emmOpen ? ' active' : ''}`}
              onClick={() => handleDropdownClick('emm')}
              style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', padding: '8px 10px', marginBottom: '0.5rem' }}
            >
              <Envelope size={14} style={{ marginRight: '8px', color: 'rgba(255, 255, 255, 0.6)' }} />
              <h6 className="nav-section-title" style={{ margin: 0, flex: 1 }}>
                EMM <span style={{ fontSize: '0.5rem', fontWeight: 'normal', opacity: 0.7 }}>(Email Marketing)</span>
              </h6>
              <span style={{ 
                marginLeft: 'auto', 
                transition: 'transform 0.2s', 
                transform: emmOpen ? 'rotate(180deg)' : 'none',
                fontSize: '0.6rem',
                color: 'rgba(255, 255, 255, 0.6)'
              }}>▲</span>
            </div>
            {emmOpen && (
              <div className="sidebar-submenu">
                {/* Unibox — pinned at top, always visible */}
                <Nav.Link
                  as={Link}
                  to="/unibox"
                  className={`nav-link ${location.pathname === '/unibox' ? 'nav-link--active' : ''}`}
                  style={{ fontWeight: 600 }}
                >
                  <Envelope className="nav-icon" size={16} />
                  Unibox
                </Nav.Link>
                <Nav.Link
                  as={Link}
                  to="/executive-assistant"
                  className={`nav-link ${location.pathname === '/executive-assistant' ? 'nav-link--active' : ''}`}
                  style={{ fontWeight: 600 }}
                >
                  <PersonWorkspace className="nav-icon" size={16} />
                  Executive Assistant
                </Nav.Link>

                {/* Campaign Ops sub-group */}
                <div
                  onClick={() => setEmmCampaignOpsOpen((p) => !p)}
                  style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', padding: '5px 10px 5px 14px', margin: '4px 0 2px', userSelect: 'none' }}
                >
                  <span style={{ flex: 1, fontSize: '0.62rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'rgba(255,255,255,0.45)' }}>
                    Campaign Ops
                  </span>
                  <span style={{ transition: 'transform 0.2s', transform: emmCampaignOpsOpen ? 'rotate(180deg)' : 'none', fontSize: '0.5rem', color: 'rgba(255,255,255,0.35)' }}>▲</span>
                </div>
                {emmCampaignOpsOpen && emmCampaignOpsItems.map((item) => {
                  const IconComponent = item.icon;
                  return (
                    <Nav.Link key={item.path} as={Link} to={item.path} className={`nav-link ${location.pathname === item.path ? 'nav-link--active' : ''}`} style={{ paddingLeft: 24 }}>
                      <IconComponent className="nav-icon" size={14} />
                      {item.label}
                    </Nav.Link>
                  );
                })}

                {/* Setup sub-group */}
                <div
                  onClick={() => setEmmSetupOpen((p) => !p)}
                  style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', padding: '5px 10px 5px 14px', margin: '4px 0 2px', userSelect: 'none' }}
                >
                  <span style={{ flex: 1, fontSize: '0.62rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'rgba(255,255,255,0.45)' }}>
                    Setup
                  </span>
                  <span style={{ transition: 'transform 0.2s', transform: emmSetupOpen ? 'rotate(180deg)' : 'none', fontSize: '0.5rem', color: 'rgba(255,255,255,0.35)' }}>▲</span>
                </div>
                {emmSetupOpen && emmSetupItems.map((item) => {
                  const IconComponent = item.icon;
                  return (
                    <Nav.Link key={item.path} as={Link} to={item.path} className={`nav-link ${location.pathname === item.path ? 'nav-link--active' : ''}`} style={{ paddingLeft: 24 }}>
                      <IconComponent className="nav-icon" size={14} />
                      {item.label}
                    </Nav.Link>
                  );
                })}

                {/* Insights sub-group */}
                <div
                  onClick={() => setEmmInsightsOpen((p) => !p)}
                  style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', padding: '5px 10px 5px 14px', margin: '4px 0 2px', userSelect: 'none' }}
                >
                  <span style={{ flex: 1, fontSize: '0.62rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'rgba(255,255,255,0.45)' }}>
                    Insights
                  </span>
                  <span style={{ transition: 'transform 0.2s', transform: emmInsightsOpen ? 'rotate(180deg)' : 'none', fontSize: '0.5rem', color: 'rgba(255,255,255,0.35)' }}>▲</span>
                </div>
                {emmInsightsOpen && emmInsightsItems.map((item) => {
                  const IconComponent = item.icon;
                  return (
                    <Nav.Link key={item.path} as={Link} to={item.path} className={`nav-link ${location.pathname === item.path ? 'nav-link--active' : ''}`} style={{ paddingLeft: 24 }}>
                      <IconComponent className="nav-icon" size={14} />
                      {item.label}
                    </Nav.Link>
                  );
                })}
              </div>
            )}
          </div>

          {/* ERM (Employee Relationship Management) Section Dropdown */}
          <div className="nav-section">
            <div 
              className={`sidebar-dropdown${ermOpen ? ' active' : ''}`}
              onClick={() => handleDropdownClick('erm')}
              style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', padding: '8px 10px', marginBottom: '0.5rem' }}
            >
              <People size={14} style={{ marginRight: '8px', color: 'rgba(255, 255, 255, 0.6)' }} />
              <h6 className="nav-section-title" style={{ margin: 0, flex: 1 }}>
                ERM <span style={{ fontSize: '0.5rem', fontWeight: 'normal', opacity: 0.7 }}>(Employee MGMT)</span>
              </h6>
              <span style={{ 
                marginLeft: 'auto', 
                transition: 'transform 0.2s', 
                transform: ermOpen ? 'rotate(180deg)' : 'none',
                fontSize: '0.6rem',
                color: 'rgba(255, 255, 255, 0.6)'
              }}>▲</span>
            </div>
            {ermOpen && (
              <div className="sidebar-submenu">
                {ermNavItems.map((item) => {
                  const IconComponent = item.icon;
                  const isActive = location.pathname === item.path || location.pathname.startsWith(`${item.path}/`);
                  return (
                    <Nav.Link
                      key={item.path}
                      as={Link}
                      to={item.path}
                      className={`nav-link ${isActive ? 'nav-link--active' : ''}`}
                    >
                      <IconComponent className="nav-icon" size={16} />
                      {item.label}
                    </Nav.Link>
                  );
                })}
              </div>
            )}
          </div>

          {/* R&E (Revenue & Expenses) Section Dropdown */}
          <div className="nav-section">
            <div 
              className={`sidebar-dropdown${reOpen ? ' active' : ''}`}
              onClick={() => handleDropdownClick('re')}
              style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', padding: '8px 10px', marginBottom: '0.5rem' }}
            >
              <CashStack size={14} style={{ marginRight: '8px', color: 'rgba(255, 255, 255, 0.6)' }} />
              <h6 className="nav-section-title" style={{ margin: 0, flex: 1 }}>
                R&E <span style={{ fontSize: '0.5rem', fontWeight: 'normal', opacity: 0.7 }}>(Revenue & Expenses)</span>
              </h6>
              <span style={{ 
                marginLeft: 'auto', 
                transition: 'transform 0.2s', 
                transform: reOpen ? 'rotate(180deg)' : 'none',
                fontSize: '0.6rem',
                color: 'rgba(255, 255, 255, 0.6)'
              }}>▲</span>
            </div>
            {reOpen && (
              <div className="sidebar-submenu">
                {reNavItems.map((item) => {
                  const IconComponent = item.icon;
                  return (
                    <Nav.Link
                      key={item.path}
                      as={Link}
                      to={item.path}
                      className={`nav-link ${
                        location.pathname === item.path ? 'nav-link--active' : ''
                      }`}
                    >
                      <IconComponent className="nav-icon" size={16} />
                      {item.label}
                    </Nav.Link>
                  );
                })}
              </div>
            )}
          </div>

          {/* Identity Management Section Dropdown */}
          <div className="nav-section">
            <div 
              className={`sidebar-dropdown${identityOpen ? ' active' : ''}`}
              onClick={() => handleDropdownClick('identity')}
              style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', padding: '8px 10px', marginBottom: '0.5rem' }}
            >
              <Shield size={14} style={{ marginRight: '8px', color: 'rgba(255, 255, 255, 0.6)' }} />
              <h6 className="nav-section-title" style={{ margin: 0, flex: 1 }}>
                IDM <span style={{ fontSize: '0.5rem', fontWeight: 'normal', opacity: 0.7 }}>(Identity MGMT)</span>
              </h6>
              <span style={{ 
                marginLeft: 'auto', 
                transition: 'transform 0.2s', 
                transform: identityOpen ? 'rotate(180deg)' : 'none',
                fontSize: '0.6rem',
                color: 'rgba(255, 255, 255, 0.6)'
              }}>▲</span>
            </div>
            {identityOpen && (
              <div className="sidebar-submenu">
                {identityNavItems.map((item) => {
                  const IconComponent = item.icon;
                  return (
                    <Nav.Link
                      key={item.path}
                      as={Link}
                      to={item.path}
                      className={`nav-link ${
                        location.pathname === item.path ? 'nav-link--active' : ''
                      }`}
                    >
                      <IconComponent className="nav-icon" size={16} />
                      {item.label}
                    </Nav.Link>
                  );
                })}
              </div>
            )}
          </div>

          {/* Admin Section Dropdown */}
          <div className="nav-section">
            <div 
              className={`sidebar-dropdown${adminOpen ? ' active' : ''}`}
              onClick={() => handleDropdownClick('admin')}
              style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', padding: '8px 10px', marginBottom: '0.5rem' }}
            >
              <Gear size={14} style={{ marginRight: '8px', color: 'rgba(255, 255, 255, 0.6)' }} />
              <h6 className="nav-section-title" style={{ margin: 0, flex: 1 }}>Admin</h6>
              <span style={{ 
                marginLeft: 'auto', 
                transition: 'transform 0.2s', 
                transform: adminOpen ? 'rotate(180deg)' : 'none',
                fontSize: '0.6rem',
                color: 'rgba(255, 255, 255, 0.6)'
              }}>▲</span>
            </div>
            {adminOpen && (
              <div className="sidebar-submenu">
                {adminNavItems.map((item) => {
                  const IconComponent = item.icon;
                  return (
                    <Nav.Link
                      key={item.path}
                      as={Link}
                      to={item.path}
                      className={`nav-link ${
                        location.pathname === item.path ? 'nav-link--active' : ''
                      }`}
                    >
                      <IconComponent className="nav-icon" size={16} />
                      {item.label}
                    </Nav.Link>
                  );
                })}
              </div>
            )}
          </div>
        </Nav>
        
        {/* Company logo + build version */}
        <div className="sidebar-version">
          {(() => {
            const version = (window as any).__BUILD_VERSION;
            const timestamp = (window as any).__BUILD_TIMESTAMP;
            const versionText =
              (!version || version === '%BUILD_VERSION%') ? 'vdev (localhost)' : `v${version}`;
            const timestampText =
              (timestamp && timestamp !== '%BUILD_TIMESTAMP%') ? String(timestamp) : null;

            const renderVersion = () => (
              <>
                <div className="sidebar-version-text">{versionText}</div>
                {timestampText ? <div className="sidebar-version-timestamp">{timestampText}</div> : null}
              </>
            );

            return (
              <>
                <div
                  className="sidebar-company-logo"
                  onClick={() => setIsLogoFlipped((v) => !v)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') setIsLogoFlipped((v) => !v);
                  }}
                  title={companyName ? `${companyName} (click to toggle version)` : 'Company logo (click to toggle version)'}
                >
                  <div className={`kg-company-flip${isLogoFlipped ? ' flipped' : ''}`}>
                    <div className="kg-company-flip-inner">
                      <div className="kg-company-flip-front">
                        {companyLogoUrl ? (
                          <img
                            className="kg-company-logo-img"
                            src={companyLogoUrl}
                            alt={companyName || 'Company logo'}
                          />
                        ) : !hasResolvedCompanyPrefs ? (
                          <div className="kg-company-logo-loading" aria-hidden="true" />
                        ) : (
                          <Logo size="medium" showText />
                        )}
                      </div>
                      <div className="kg-company-flip-back">
                        {renderVersion()}
                      </div>
                    </div>
                  </div>
                </div>

              </>
            );
          })()}
        </div>
      </div>
    </div>
    </>
  );
};

export default Sidebar;
